import { fetchAuthSession } from 'aws-amplify/auth'; // Import fetchAuthSession
import { Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';

const NotAssigned = () => {
  // Log screen view
  useScreenLogging('NotAssignedOfficers', {
    component: 'NotAssignedOfficers',
    screenType: 'not-assigned',
  });
    const [searchTerm, setSearchTerm] = useState('');
    const [officers, setOfficers] = useState([]);
    const [loadingOfficers, setLoadingOfficers] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [error, setError] = useState(null); // State for API errors

    const searchInputRef = useRef(null);
    const suggestionsBoxRef = useRef(null);

    const BASE_URL = 'https://api.innoworx.ai/Stage';

    // Function to get the authentication token
    const getAuthToken = async () => {
        try {
            const { tokens } = await fetchAuthSession();
            if (tokens && tokens.idToken) {
                return tokens.idToken.toString();
            } else {
                console.error('No ID Token found in the current session.');
                return null;
            }
        } catch (error) {
            console.error('Error fetching authentication session:', error);
            return null;
        }
    };

    // Function to fetch not assigned officers from the API
    useEffect(() => {
        const fetchNotAssignedOfficers = async () => {
            setLoadingOfficers(true);
            setError(null); // Clear previous errors

            if (BASE_URL === 'YOUR_API_BASE_URL') {
                setError("API Base URL is not configured. Please update BASE_URL in NotAssigned.js.");
                setOfficers([]);
                setFilteredSuggestions([]);
                setLoadingOfficers(false);
                return;
            }

            try {
                const token = await getAuthToken();
                if (!token) {
                    setError('Authentication token is missing. Please ensure a user is logged in.');
                    setLoadingOfficers(false);
                    return;
                }

                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                };

                // Using the /user/officer/status-list endpoint for inactive officers
                const response = await fetch(`${BASE_URL}/user/officer/status-list`, {
                    method: 'GET',
                    headers: headers,
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
                }

                const responseData = await response.json();
                // Access the 'users' property which contains the array of officers
                let data = responseData.users;
                console.log("API response data for NotAssigned (after extraction):", data); // Log the extracted data for inspection

                // IMPORTANT: Check if data is an array before filtering
                if (!Array.isArray(data)) {
                    console.warn("API response for not assigned officers is not an array (expected 'users' array):", data);
                    data = []; // Treat as empty array to prevent .filter() error
                    setError("Received unexpected data format from API. Please check console for details.");
                }

                // Filter for 'inactive' officers based on the 'status' field
                const notAssignedOfficers = data.filter(officer =>
                    officer.status === 'inactive'
                );

                // Filter the not assigned officers based on the current search term
                const filteredDisplayOfficers = notAssignedOfficers.filter(officer =>
                    (officer.user_name && officer.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (officer.employee_id && officer.employee_id.includes(searchTerm)) // Changed to employee_id for search
                );
                setOfficers(filteredDisplayOfficers);

                // Update suggestions based on all fetched not assigned data
                const uniqueSuggestions = Array.from(new Set(
                    notAssignedOfficers.map(officer => officer.user_name) // Use user_name for suggestions
                ));
                setFilteredSuggestions(uniqueSuggestions);

            } catch (err) {
                console.error("Error fetching not assigned officers:", err);
                setError(`Failed to load unassigned officers. Please try again. Error: ${err.message}`);
                setOfficers([]); // Clear officers on error
                setFilteredSuggestions([]); // Clear suggestions on error
            } finally {
                setLoadingOfficers(false);
            }
        };

        const timer = setTimeout(() => {
            fetchNotAssignedOfficers();
        }, 300); // Debounce search for better performance

        return () => clearTimeout(timer); // Cleanup timer

    }, [searchTerm, BASE_URL]); // Re-run when search term or BASE_URL changes

    // Handle outside click to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                searchInputRef.current && !searchInputRef.current.contains(event.target) &&
                suggestionsBoxRef.current && !suggestionsBoxRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearchInputChange = (e) => {
        setSearchTerm(e.target.value);
        setShowSuggestions(e.target.value.length > 0);
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchTerm(suggestion);
        setShowSuggestions(false);
    };

    return (
        <div className="flex flex-col flex-1 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] overflow-hidden">

            {/* Header Title */}
            <div className="mb-6 ">
                <h1 className="text-black text-3xl font-bold font-['Figtree'] leading-10">Not Assigned Officers</h1>
            </div>

            {/* Search Bar */}
            <div data-property-1="Normal" className="w-96 h-14 pl-4 pr-3.5 py-2.5 mb-6 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] backdrop-blur-[30px] flex flex-col justify-center items-start gap-2.5 relative">


                <div className="inline-flex justify-start items-center gap-4 w-full">
                    <Search className="w-6 h-6 text-neutral-400" />
                    <input
                        type="text"
                        placeholder="Search by Employee name or id"
                        className="flex-grow bg-transparent outline-none text-neutral-900 text-lg font-bold font-['Figtree'] leading-snug placeholder-neutral-400"
                        value={searchTerm}
                        onChange={handleSearchInputChange}
                        onFocus={() => setShowSuggestions(searchTerm.length > 0)}
                        ref={searchInputRef}
                    />
                </div>
                {/* Search Suggestions Dropdown */}
                {showSuggestions && searchTerm.length > 0 && filteredSuggestions.length > 0 && (
                    <div ref={suggestionsBoxRef} className="absolute z-10 w-full bg-neutral-50/90 rounded-[10px] top-full mt-2 left-0 shadow-lg max-h-60 overflow-y-auto">
                        <div className="inline-flex flex-col justify-start items-center gap-3 w-full p-3">
                            {filteredSuggestions.map((suggestion, index) => (
                                <React.Fragment key={index}>
                                    <div
                                        className="self-stretch inline-flex justify-center items-center gap-2.5 cursor-pointer hover:bg-gray-100 p-2 rounded-md"
                                        onClick={() => handleSuggestionClick(suggestion)}
                                    >
                                        <div className="w-full justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                                            {suggestion}
                                        </div>
                                    </div>
                                    {index < filteredSuggestions.length - 1 && (
                                        <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400"></div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Officer Cards Container */}
            <div className="flex flex-wrap justify-start items-start gap-4 w-full overflow-y-auto flex-1 pb-5 pt-0">

                {loadingOfficers ? (
                    <div className="flex items-center justify-center w-full h-full text-gray-500">Loading Unassigned Officers...</div>
                ) : error ? (
                    <div className="flex items-center justify-center w-full h-full text-red-500">{error}</div>
                ) : officers.length > 0 ? (
                    officers.map((officer) => (
                        <div key={officer.user_id} className="p-4 bg-neutral-50/50 rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] backdrop-blur-[20px] inline-flex flex-col justify-start items-start gap-4 flex-grow-0 flex-shrink-0 basis-[300px]">
                            <div className="w-full flex flex-col justify-start items-start gap-3">
                                <div className="self-stretch inline-flex justify-between items-center">
                                    <div className="justify-center text-neutral-900 text-lg font-bold font-['Figtree'] leading-snug">{officer.user_name}</div>
                                </div>
                                <div className="self-stretch inline-flex justify-between items-center">
                                    <div className="text-center justify-center text-neutral-500 text-base font-semibold font-['Open_Sans'] leading-tight">{officer.spec_role}</div> {/* Changed to spec_role */}
                                    <div className="text-right justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">ID : {officer.employee_id || officer.user_id}</div> {/* Display employee_id, fallback to user_id */}
                                </div>
                            </div>
                            {/* No site details or duty status for Not Assigned officers as per template */}
                            {/* The 'status' field from the API response can be displayed if needed, but not filtered by 'on_duty' */}
                            <div className="w-full p-3 bg-neutral-50/10 rounded-xl shadow-[inset_0px_4px_12px_0px_rgba(0,0,0,0.05)] flex flex-col justify-start items-start gap-1.5">
                                <div className="self-stretch justify-center text-neutral-900 text-lg font-normal font-['Open_Sans'] leading-relaxed">Status : {officer.status || 'N/A'}</div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="flex items-center justify-center w-full h-full text-gray-500">No unassigned officers found.</div>
                )}
            </div>
        </div>
    );
};

export default NotAssigned;
